import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AdminUser, BackfillResult, getAdminUsers, startOmdbBackfill } from '../api';

export default function AdminPage() {
  const navigate = useNavigate();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Backfill state
  const [backfillRunning, setBackfillRunning] = useState(false);
  const [backfillResult, setBackfillResult] = useState<BackfillResult | null>(null);
  const [backfillError, setBackfillError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchUsers() {
      try {
        const data = await getAdminUsers();
        setUsers(data);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load users');
      } finally {
        setLoading(false);
      }
    }

    fetchUsers();
  }, []);

  const handleBackfill = async () => {
    setBackfillRunning(true);
    setBackfillResult(null);
    setBackfillError(null);

    try {
      const result = await startOmdbBackfill(50);
      setBackfillResult(result);
    } catch (err) {
      setBackfillError(err instanceof Error ? err.message : 'Failed to run backfill');
    } finally {
      setBackfillRunning(false);
    }
  };

  if (loading) {
    return (
      <div className="card">
        <p>Loading users...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card">
        <p className="error">{error}</p>
      </div>
    );
  }

  return (
    <div>
      <h2>Admin Tools</h2>

      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <Link to="/admin/feedback" className="admin-tool-link">
          <div>
            <h3 style={{ margin: 0 }}>User Feedback</h3>
            <p style={{ margin: '4px 0 0', fontSize: '13px', opacity: 0.6 }}>
              Review bug reports, feature requests, and questions from users
            </p>
          </div>
          <span style={{ fontSize: '20px', opacity: 0.4 }}>&rsaquo;</span>
        </Link>
      </div>

      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <h3 style={{ marginTop: 0, marginBottom: '1rem' }}>OMDB Metadata Backfill</h3>
        <p style={{ marginBottom: '1rem', color: '#888' }}>
          Backfill missing IMDb, Rotten Tomatoes, and Metacritic ratings for movies.
        </p>

        <button
          onClick={handleBackfill}
          disabled={backfillRunning}
          className="btn btn-primary"
          style={{ marginBottom: '1rem' }}
        >
          {backfillRunning ? 'Running...' : 'Start Backfill (50 movies)'}
        </button>

        {backfillError && <p className="error">{backfillError}</p>}

        {backfillResult && (
          <div
            style={{
              padding: '1rem',
              backgroundColor: backfillResult.rateLimited ? '#442' : '#242',
              borderRadius: '8px',
            }}
          >
            <p style={{ margin: 0, fontWeight: 'bold' }}>
              {backfillResult.status === 'completed' ? 'Completed' : 'Stopped'}
              {backfillResult.rateLimited && ' (Rate Limited)'}
            </p>
            <ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.5rem' }}>
              <li>
                Total missing: {backfillResult.total} | Processed: {backfillResult.processed}
              </li>
              <li>Updated: {backfillResult.updated}</li>
              <li>Skipped: {backfillResult.skipped}</li>
              <li>Errors: {backfillResult.errors}</li>
            </ul>
            {backfillResult.rateLimited && (
              <p style={{ margin: '0.5rem 0 0', color: '#fa5' }}>
                OMDB rate limit hit. Try again later.
              </p>
            )}
          </div>
        )}
      </div>

      <h2>Users ({users.length})</h2>

      {users.length === 0 ? (
        <div className="card">
          <p>No users found.</p>
        </div>
      ) : (
        <div className="card">
          <table className="admin-users-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Username</th>
                <th>Email</th>
                <th>Playlists</th>
                <th>Movies</th>
                <th>Joined</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr
                  key={user.id}
                  className="clickable-row"
                  onClick={() => navigate(`/admin/users/${user.id}`)}
                >
                  <td>
                    <div className="user-cell">
                      {user.picture && (
                        <img src={user.picture} alt="" className="user-avatar-small" />
                      )}
                      <span>{user.name || '(No name)'}</span>
                    </div>
                  </td>
                  <td>{user.username || '(Not set)'}</td>
                  <td>{user.email}</td>
                  <td>{user.playlist_count}</td>
                  <td>{user.movie_count}</td>
                  <td>{new Date(user.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
